import React, { useState } from 'react'
import { get } from 'lodash'
import styled from 'styled-components'
import { SectionStyle } from '../index.styled'
import EditableText from '../components/EditableText'
import { CircleEditButton } from '../components/Widgets'
import { FlexBox } from '../../../components/UI/FlexBox'
import { HSDScoreConfig, HSDSpiderGraph } from '../components/HSDSpiderGraph'
import { HSDTriangleChart } from '../components/HSDTriangleGraph'
import { compactMap } from '../../../utils/array+extensions'
import { HSDSelector } from '../components/HSDSelector'
import HideWrap from '../../../components/assessmentReview/comp/hideWrap'
import ConditionalHideWrap from '../../../components/assessmentReview/comp/ConditionalHidewrap'

const Container = styled.section`
  ${SectionStyle}

  .note {
    flex-grow: 1;
    margin-bottom: 60px;
  }

  button {
    margin-left: 40px;
  }
`

const NOTES_FIELDS = {
  HSD_SUMMARY_NOTE: 'hsd_summary_note',
  LIFESTYLE_INTEGRATION_NOTE: 'lifestyle_integration_note'
}

export default function HSDSummarySection(props) {
  const { data, notes, handleCommendSave, person, editabled, view, hideSection, hideSectionHandle} = props
  const [noteEditing, setNoteEditing] = useState(false)
  const [intergrationTextEditing, setIntegrationTextEditing] = useState(false)
  const note = notes.find((n) => n.pillar === NOTES_FIELDS.HSD_SUMMARY_NOTE)
  const integrationNote = notes.find(
    (n) => n.pillar === NOTES_FIELDS.LIFESTYLE_INTEGRATION_NOTE
  )
  const graphData = get(data, '0.snapshot', {})
  const membership = get(person, 'profile.membership')
  return (
    <Container>
      <h1 className="ar-section-title">Health Span Domains Summary</h1>
      <FlexBox justifyContent="space-between">
        <EditableText
          className={'note'}
          value={(note && note.memo) || '-'}
          editing={noteEditing}
          setEditing={setNoteEditing}
          onSave={(text) => {
            handleCommendSave(text, NOTES_FIELDS.HSD_SUMMARY_NOTE)
          }}
        />
        {!noteEditing && view ==='Draft' && (
          <CircleEditButton
            onClick={() => {
              setNoteEditing(true)
            }}
          />
        )}
      </FlexBox>
      <h2 className="ar-section-group-title">Physical Assessment Results</h2>
      {membership === 'basic' ? (
        <div
          style={{
            padding: '40px',
            textAlign: 'center'
          }}
        >
          <HSDTriangleChart {...graphData} />
        </div>
      ) : (
        <div
          style={{
            padding: '40px'
          }}
        >
          <HSDSpiderGraph {...graphData} />
        </div>
      )}
      <KeyWinsOrImprovement
        {...{ graphData, handleCommendSave, notes, editabled, person, view, hideSection, hideSectionHandle}}
      />
      <div style={{marginTop:'20px'}}>
        <ConditionalHideWrap
          name="Integration"
          title="Integration"
          syncStatus={hideSectionHandle}
          status={hideSection}
          view={view}
          condition={
            view === 'Draft' || (view !== 'Draft' && !hideSection.includes('Integration'))
          }
        >
      <h2
        className="ar-section-group-title"
        style={{
          marginTop: 36,
          marginBottom: 12
        }}
      >
        Integration
      </h2>
      <FlexBox>
        <EditableText
          className={'note'}
          value={(integrationNote && integrationNote.memo) || ''}
          placeholder={editabled ? 'Please type here.' : '-'}
          editing={intergrationTextEditing}
          setEditing={setIntegrationTextEditing}
          onSave={(text) => {
            handleCommendSave(text, NOTES_FIELDS.LIFESTYLE_INTEGRATION_NOTE)
          }}
        />
        {!intergrationTextEditing && editabled && (
          <CircleEditButton
            style={{
              marginLeft: '17px'
            }}
            onClick={() => {
              setIntegrationTextEditing(true)
            }}
          />
        )}
      </FlexBox>
      </ConditionalHideWrap>
      </div>
    </Container>
  )
}

const KeyWinsOrImprovementContainer = styled.div`
  border: 1px solid #e6e9f2;
  margin-top: 36px;
  background-color: rgba(250, 250, 250, 1);
  display: flex;
  .notes-half-container {
    padding: 16px 36px;
    color: rgba(50, 63, 74, 1);
    width: 50%;
    &:first-child {
      border-right: 1px solid rgba(230, 233, 242, 1);
    }
    .notes-title {
      padding: 24px 0;
      font-size: 24px;
      line-height: 1.5;
      letter-spacing: 0.3px;

      border-bottom: 1px solid rgba(230, 233, 242, 1);
    }

    .single-note-container {
      padding: 16px 0;

      .note-pillar-title {
        font-size: 20px;
        line-height: 1.5;
        margin-bottom: 16px;
      }

      .domain-score {
        font-size: 24px;
        font-weight: 700;
      }
    }
  }
`

const DomainName = {
  muscular_fitness: 'Muscle Fitness',
  joint_fitness: 'Movement Quality',
  balance: 'Balance',
  cognitive_health: 'Cognitive Health',
  body_composition: 'Body Composition',
  aerobic_fitness: 'Aerobic Fitness'
}

const KeyWinsOrImprovement = (props) => {
  const { editabled, handleCommendSave, notes, person, view, hideSection, hideSectionHandle} = props

  const domains = Object.keys(props.graphData).map((domainKey) => {
    const domain = props.graphData[domainKey]
    const score = get(domain, 'score_manual.value') || get(domain, 'score_auto')
    const config = score && HSDScoreConfig(score)
    return {
      color: config ? config.color : 'grey',
      score: score,
      key: domainKey
    }
  })

  const keyWinNotes = compactMap(
    ['hsd_key_wins_1_note', 'hsd_key_wins_2_note', 'hsd_key_wins_3_note'],
    (noteKey) => notes.find((n) => n.pillar === noteKey)
  )

  const keyImprovementNotes = compactMap(
    [
      'hsd_key_improvement_1_note',
      'hsd_key_improvement_2_note',
      'hsd_key_improvement_3_note'
    ],
    (noteKey) => notes.find((n) => n.pillar === noteKey)
  )

  return (
    <KeyWinsOrImprovementContainer>
      <div className="notes-half-container">
        <ConditionalHideWrap
          name="Key Wins - HSD"
          title="Key Wins"
          syncStatus={hideSectionHandle}
          status={hideSection}
          view={view}
          isKeyWins={true}
          condition={
            view === 'Draft' || (view !== 'Draft' && !hideSection.includes('Key Wins - HSD'))
          }
        >
        <div className="notes-title">Key Wins:</div>

        {keyWinNotes.map((note, index) => {
          const domain = domains.find((d) => d.key === note.memo)
          return (
            <div className="single-note-container" key={index}>
              {editabled ? (
                <HSDSelector {...{ note, handleCommendSave, person }} />
              ) : (
                <div className="note-pillar-title">
                  {DomainName[domain.key]}
                </div>
              )}
              <div
                className="domain-score"
                style={{
                  color: (domain && domain.color) || 'grey'
                }}
              >
                {(domain && domain.score) || '-'}
              </div>
            </div>
          )
        })}
        </ConditionalHideWrap>
      </div>
      <div className="notes-half-container">
        <ConditionalHideWrap
          name="Area of Improvement - HSD"
          title="Area of Improvement:"
          syncStatus={hideSectionHandle}
          status={hideSection}
          view={view}
          isKeyWins={true}
          condition={
            view === 'Draft' || (view !== 'Draft' && !hideSection.includes('Area of Improvement - HSD'))
          }
        >
        <div className="notes-title">Area of Improvement:</div>

        {keyImprovementNotes.map((note, index) => {
          const domain = domains.find((d) => d.key === note.memo)
          return (
            <div className="single-note-container" key={index}>
              {editabled ? (
                <HSDSelector {...{ note, handleCommendSave, person }} />
              ) : (
                <div className="note-pillar-title">
                  {DomainName[domain.key]}
                </div>
              )}
              <div
                className="domain-score"
                style={{
                  color: (domain && domain.color) || 'grey'
                }}
              >
                {(domain && domain.score) || '-'}
              </div>
            </div>
          )
        })}
        </ConditionalHideWrap>
      </div>
    </KeyWinsOrImprovementContainer>
  )
}
